import { IAppContext } from 'App/AppContext';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import { isAuditCriteriaPreviewEnabled, isSkillControlEnabled } from 'models/copilot/copilotPreviewFeatureToggles';
import { AIEnabled } from 'models/setting';
import { FeatureTypes, hasUserDataPermission, hasUserFeature } from 'services/Auth/featurePermissions';

/**
 * Whether the Copilot panel can actually answer: the license feature, the tenant setting and a tenant
 * that is past onboarding. The panel itself renders nothing without the feature and shows its
 * activation state without the setting, so a scene that offers AI must ask the same question.
 */
export const isCopilotPanelAvailable = (appContext: IAppContext): boolean => {
  if (!hasUserFeature(appContext, FeatureTypes.AIAssistant)) return false;
  if ((appContext.globalDataCache.settings.get(AIEnabled) as boolean) !== true) return false;

  return !appContext.isOnboardingInProgress;
};

/**
 * Whether a scene may offer Draft automation (phase 22): the panel and the SkillControl switch (the preview toggle
 * skillControl plus the organization's setting SkillControlEnabled), the same the API checks. A scene puts the
 * answer on the content it registers (canDraftAutomation); the definitions list the action from that flag, never
 * from the app context, which the panel's bridge handler holds stale.
 */
export const isAutomationDraftingAvailable = (appContext: IAppContext): boolean =>
  isCopilotPanelAvailable(appContext) && isSkillControlEnabled(appContext.globalDataCache.settings);

/**
 * Whether a detail scene may offer Audit with AI on its entity (PLAN-077): the panel, the Audit preview and the
 * user's update right on the entity, the outcome writer's own gate, so a reader never sees the action. A scene puts
 * the answer on the content it registers (canAuditEvidence), as for Draft automation.
 */
export const isEntityEvidenceAuditAvailable = (appContext: IAppContext, authSchemaId: number | undefined): boolean =>
  isCopilotPanelAvailable(appContext) &&
  isAuditCriteriaPreviewEnabled(appContext) &&
  hasUserDataPermission(appContext, [authSchemaId], AuthSchemaLineOperation.Update);

/**
 * Whether a Supplier asset may offer the compliance check and its certificate surfaces (PLAN-CTI-098, D12): the panel
 * and the Audit preview, since the check's rows come from the audit criteria. A scene puts the answer on the content
 * it registers (canCheckSupplierCompliance), as for Draft automation.
 */
export const isSupplierComplianceAvailable = (appContext: IAppContext): boolean =>
  isCopilotPanelAvailable(appContext) && isAuditCriteriaPreviewEnabled(appContext);

/**
 * Whether the panel answers a typed or ready-made question. The panel answers one whenever it is available,
 * so a Prompt binding needs the same gate as the panel.
 */
export const isCopilotChatAvailable = (appContext: IAppContext): boolean => isCopilotPanelAvailable(appContext);

/**
 * Whether the panel of this browser tab is running something: a chat run (the panel's loading or streaming state) or
 * a process it started. A scene disables every AI entry point that would start a second run through the panel.
 */
export const isCopilotBusy = (appContext: IAppContext): boolean =>
  appContext.copilotBridge.isBusy || appContext.copilotBridge.isLoading;
